import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  integrationErrorMessage as errorMessage,
  httpStatus,
  integrationApi,
} from "./client";
import { AuthFailure, dateTime, number, SectionHeading } from "./shared";
import "./IntegrationTests.css";
import {
  IntegrationQuickStart,
  QuickTestConfig,
} from "./IntegrationQuickStart";
import { IntegrationScenario } from "./IntegrationScenario";
import { IntegrationLivePopup } from "./IntegrationLivePopup";
import { RunAssessment, TestStandards } from "./IntegrationTestAssessment";

export type Live = {
  seq: number;
  title: string;
  status: string;
  storeName: string;
  storeSeq?: number;
  prepared?: number;
  start: string;
  finish: string;
  playback?: string;
  stop?: string;
  stopError?: string;
  publisher?: PublisherState | null;
};
export type PublisherState = {
  state: string;
  cameraMuted?: boolean;
  micMuted?: boolean;
  mirrored?: boolean;
  endsAt?: string;
  frames?: number;
  error?: string;
  warning?: string;
};
type Config = QuickTestConfig;
type Metric = {
  attempts: number;
  success: number;
  failed: number;
  bytes: number;
  meanMs: number | null;
  p95UpperMs: number | null;
  maxMs?: number | null;
  latencyLimitMs?: number;
  overBudget?: number;
};
export type Run = {
  id: string;
  liveSeq: number;
  state: string;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  updatedAt?: string;
  config: Config;
  error?: string;
  assessment?: {
    status: string;
    issues: { key: string; message: string; count: number }[];
    unchecked: string[];
  };
  result?: {
    connected: number;
    peakConnected: number;
    received: number;
    chatAcknowledged: number;
    ordersCompleted: number;
    ordersUnknown: number;
    metrics: Record<string, Metric>;
    errors: string[];
  };
  inventory?: Inventory;
  notifications?: Record<
    string,
    {
      total: number;
      queued: number;
      processed: number;
      validated: number;
      failed: number;
      providerRequests: number;
      dryRun: boolean;
    }
  >;
};
type Inventory = {
  passed: boolean;
  at: string;
  receipts: number;
  details: number;
  duplicateDetails: number;
  missingQuantity: number;
  items: {
    product: number;
    option: number | null;
    name: string;
    optionName?: string;
    initial: number;
    sold: number;
    restored: number;
    expected: number;
    actual: number;
    redis: number | null;
    passed: boolean;
  }[];
};
export type Board = {
  stores: { seq: number; store_name: string }[];
  lives: Live[];
  runs: Run[];
  runner: {
    host: string;
    chat: boolean;
    notification: boolean;
    media: string;
    publisher?: boolean;
  };
};
export type Detail = {
  status: string;
  testUser: string;
  products: any[];
  notices: any[];
  qa: any[];
  stop?: string;
  stopError?: string;
  automation?: { name: string; status: string; priceMode: string; at: string };
  publisher?: PublisherState | null;
};
const tabs = [
  ["lives", "라이브 준비"],
  ["load", "부하 실행"],
  ["functional", "실제 기능"],
  ["results", "결과·재고"],
  ["cost", "비용 예상"],
] as const;
type TestTab = (typeof tabs)[number][0];
const base = "/integration-tests";
const active = (run: Run) =>
  ["scheduled", "running", "draining", "notifications"].includes(run.state);
const states: Record<string, string> = {
  scheduled: "시작 예약",
  running: "실행 중",
  draining: "주문 결과 확인",
  notifications: "정산톡 큐 처리",
  completed: "실행 종료",
  cancelled: "중지됨",
  failed: "실행 실패",
  interrupted: "서버 연결 끊김",
};
const metricNames: Record<string, string> = {
  chatConnect: "채팅 연결",
  chatSend: "채팅 전송",
  media: "영상 다운로드",
  api: "목록·API 묶음",
  orders: "구매 완료",
  cart: "장바구니·구매내역",
  cancel: "주문 취소",
  seller: "상품 상태·판매 노출",
  waitlist: "재입고 대기·취소·구매",
};
const localTime = (date: Date) =>
  new Date(date.getTime() + 9 * 3600000).toISOString().slice(0, 16);
const iso = (value: string) => new Date(`${value}:00+09:00`).toISOString();
const defaults: Config = {
  viewers: 3000,
  rampSeconds: 60,
  durationSeconds: 600,
  chatEverySeconds: 15,
  apiEverySeconds: 10,
  purchaseEverySeconds: 30,
  video: true,
  chat: true,
  api: true,
  orders: true,
  cart: true,
  cancel: true,
  sampleMessages: 50000,
  statements: true,
  startAt: "",
  seller: true,
  waitlist: true,
  autoStock: true,
  productEverySeconds: 5,
  stockPerItem: 10000,
  warehouseProducts: 100,
};

export function IntegrationTestsView({
  onAuth,
  initialLive = 0,
}: {
  onAuth: AuthFailure;
  initialLive?: number;
}) {
  const [popupLive, setPopupLive] = useState(initialLive);
  const [advanced, setAdvanced] = useState(false);
  const [tab, setTab] = useState<TestTab>("lives");
  const [board, setBoard] = useState<Board | null>(null),
    [error, setError] = useState(""),
    [pollError, setPollError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState("");
  const [selected, setSelected] = useState<number[]>([]),
    [focus, setFocus] = useState(0),
    [detail, setDetail] = useState<Detail | null>(null);
  const [config, setConfig] = useState(defaults),
    [startAt, setStartAt] = useState(""),
    [inventory, setInventory] = useState<Inventory | null>(null);
  const [runId, setRunId] = useState(""),
    [quote, setQuote] = useState<number | null>(null),
    [createOpen, setCreateOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false),
    [quickError, setQuickError] = useState("");
  const [stopping, setStopping] = useState(false);
  const stopRequested = useRef(false),
    stopLock = useRef(false);
  const nextHour = new Date(Math.ceil((Date.now() + 1000) / 3600000) * 3600000);
  const [create, setCreate] = useState({
    storeSeq: 0,
    title: "통합 부하 테스트",
    capacity: 5000,
    start: localTime(nextHour),
    finish: localTime(new Date(nextHour.getTime() + 3600000)),
  });
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const lock = useRef(false),
    mounted = useRef(true);
  const fail = useCallback(
    (e: unknown, polling = false) => {
      const status = httpStatus(e);
      if (status === 401 || status === 403) onAuth(status);
      (polling ? setPollError : setError)(errorMessage(e));
    },
    [onAuth],
  );
  const refresh = useCallback(async () => {
    const { data } = await integrationApi.get<Board>(base);
    if (!mounted.current) return;
    setBoard(data);
    setPollError("");
    setFocus((current) =>
      data.lives.some((live) => live.seq === current)
        ? current
        : data.lives[0]?.seq || 0,
    );
    setRunId((current) => current || data.runs[0]?.id || "");
  }, []);
  const inspect = useCallback(async () => {
    if (!focus) return;
    const { data } = await integrationApi.get<Detail>(`${base}/lives/${focus}`);
    if (mounted.current) setDetail(data);
  }, [focus]);
  useEffect(() => {
    mounted.current = true;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        await refresh();
      } catch (e) {
        if (!disposed) fail(e, true);
      } finally {
        if (!disposed) timer = setTimeout(poll, 3000);
      }
    };
    void poll();
    return () => {
      disposed = true;
      mounted.current = false;
      clearTimeout(timer);
    };
  }, [refresh, fail]);
  useEffect(() => {
    setDetail(null);
    setInventory(null);
    let disposed = false,
      timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        await inspect();
        if (!disposed) setPollError("");
      } catch (e) {
        if (!disposed) fail(e, true);
      } finally {
        if (!disposed) timer = setTimeout(poll, 3000);
      }
    };
    if (focus) void poll();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [inspect, fail, focus]);
  const perform = async (label: string, action: () => Promise<unknown>) => {
    if (lock.current || stopLock.current) return;
    lock.current = true;
    setBusy(label);
    setError("");
    setMessage("");
    try {
      await action();
      setMessage(
        label === "선택 라이브 부하 실행" && stopRequested.current
          ? "부하 중지를 요청하고 추가 실행 접수를 중단했습니다."
          : `${label} 처리했습니다.`,
      );
      await refresh();
      await inspect();
    } catch (e) {
      fail(e);
    } finally {
      lock.current = false;
      setBusy("");
    }
  };
  const post = (path: string, body?: unknown) =>
    integrationApi.post(base + path, body, { timeout: 300000 });
  const change = <K extends keyof Config>(key: K, value: Config[K]) =>
    setConfig((current) => ({ ...current, [key]: value }));
  const toggle = (seq: number) =>
    setSelected((current) =>
      current.includes(seq)
        ? current.filter((id) => id !== seq)
        : [...current, seq],
    );
  const currentRun =
    board?.runs.find((run) => run.id === runId) || board?.runs[0];
  const selectedLive = board?.lives.find((live) => live.seq === focus);
  const selectedCount = selected.length;
  const stopAllTests = async () => {
    if (stopLock.current) return;
    stopLock.current = true;
    stopRequested.current = true;
    setStopping(true);
    setError("");
    setQuickError("");
    try {
      const { data } = await post("/stop-all");
      setBusy((current) =>
        current ? "중지 요청됨 · 준비 마무리 대기" : current,
      );
      setMessage(
        `${data.requested}개 부하${data.streamsRequested ? `와 ${data.streamsRequested}개 영상 송출` : ""}에 중지를 요청했습니다. 추가 실행 접수도 중단했습니다. 이미 접수된 주문은 마무리될 수 있습니다.`,
      );
      await refresh().catch((e) => fail(e, true));
    } catch (e) {
      const explanation = `중지 요청에 실패했습니다. 다시 중지해 주세요. ${errorMessage(e)}`;
      setError(explanation);
      setQuickError(explanation);
    } finally {
      stopLock.current = false;
      setStopping(false);
    }
  };
  const startTest = async (seq: number, settings: Config) => {
    const { data } = await post(`/lives/${seq}/runs`, settings);
    // A request can reach the server after stop-all. Stop that late response too.
    if (stopRequested.current) await post(`/runs/${data.id}/stop`);
    return data;
  };
  const runQuick = async (liveSeqs: number[], quickConfig: Config) => {
    if (lock.current || stopLock.current) return;
    lock.current = true;
    stopRequested.current = false;
    setQuickError("");
    setError("");
    setMessage("");
    const accepted: { seq: number; id: string }[] = [];
    const errors: string[] = [];
    setBusy("테스트 계정·상품 준비 중…");
    try {
      for (const [index, seq] of liveSeqs.entries()) {
        if (stopRequested.current) break;
        setBusy(
          `계정·상품 준비 및 실행 접수 중 (${index + 1}/${liveSeqs.length})`,
        );
        try {
          // Each start already prepares users, warehouse products and stock.
          // Refresh the timestamp after preparing earlier lives in this batch.
          const data = await startTest(seq, {
            ...quickConfig,
            startAt: new Date(Date.now() + 3000).toISOString(),
          });
          accepted.push({ seq, id: data.id });
        } catch (e) {
          errors.push(`#${seq}: ${errorMessage(e)}`);
          if (httpStatus(e) === 401 || httpStatus(e) === 403) {
            fail(e);
            break;
          }
        }
      }
      if (accepted.length) {
        setConfig(quickConfig);
        setStartAt("");
        setSelected(accepted.map((run) => run.seq));
        setFocus(accepted[0].seq);
        setRunId(accepted[0].id);
        setTab("results");
        if (!stopRequested.current)
          setMessage(
            `${accepted.length}개 라이브의 간편 부하 테스트를 접수했습니다. 결과에서 실행 상태를 확인하세요.`,
          );
      }
      if (errors.length) {
        const explanation = `${accepted.length}개 접수 · ${errors.join(" / ")}`;
        setQuickError(explanation);
        setError(explanation);
      }
      await refresh().catch((e) => fail(e, true));
      if (stopRequested.current)
        setMessage(
          "추가 부하 실행 접수를 중단했습니다. 결과에서 중지 상태를 확인하세요.",
        );
    } finally {
      lock.current = false;
      setBusy("");
      if (accepted.length) setQuickOpen(false);
    }
  };
  const runAll = () =>
    perform("선택 라이브 부하 실행", async () => {
      stopRequested.current = false;
      const errors: string[] = [];
      const accepted: string[] = [];
      for (const seq of selected) {
        if (stopRequested.current) break;
        try {
          const data = await startTest(seq, {
            ...config,
            startAt: startAt
              ? iso(startAt)
              : new Date(Date.now() + 3000).toISOString(),
          });
          accepted.push(data.id);
        } catch (e) {
          errors.push(`#${seq}: ${errorMessage(e)}`);
        }
      }
      if (accepted.length) {
        setRunId(accepted[0]);
        setTab("results");
      }
      if (errors.length)
        throw new Error(
          `${accepted.length}개 실행 예약. ${errors.join(" / ")}`,
        );
    });
  const exportRun = () => {
    if (!currentRun) return;
    const blob = new Blob([JSON.stringify(currentRun, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `fanbird-integration-${currentRun.id}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };
  if (!board)
    return (
      <div
        className="pa-card pa-test-loading"
        role={error || pollError ? "alert" : "status"}
      >
        {error || pollError || "통합 테스트 실행 환경을 불러오고 있습니다."}
        {(error || pollError) && (
          <button
            className="pa-button"
            onClick={() => void refresh().catch(fail)}
          >
            다시 불러오기
          </button>
        )}
      </div>
    );
  return (
    <div className="pa-integration">
      <div className="pa-test-intro">
        <div>
          <h2>여러 셀러가, 각자의 라이브에서.</h2>
          <p>
            셀러당 라이브 하나로 영상·채팅·조회·구매·취소를 동시에 실행하고
            재고를 대조합니다.
          </p>
        </div>
        <div className="pa-test-intro-actions">
          <span className="pa-test-dry-badge">시작톡·정산톡 실제 발송 OFF</span>
          <button
            type="button"
            className="pa-button pa-button-primary"
            disabled={!!busy || stopping}
            onClick={() => {
              setQuickError("");
              setQuickOpen(true);
            }}
          >
            간편 부하 테스트
          </button>
          <button
            type="button"
            className="pa-button pa-button-danger pa-test-stop-all"
            disabled={stopping}
            onClick={() => void stopAllTests()}
          >
            {stopping ? "중지 요청 중…" : "부하 테스트 중지"}
          </button>
        </div>
      </div>
      <TestStandards />
      {board.runs
        .filter((run) => run.assessment?.status === "error")
        .map((run) => (
          <div
            key={run.id}
            className="pa-test-assessment pa-test-assessment-error"
            role="alert"
          >
            <strong>라이브 #{run.liveSeq} · 오류 감지</strong>
            <small>
              {states[run.state] || run.state} ·{" "}
              {dateTime(run.updatedAt || run.createdAt)}
            </small>
            <span>
              {run.assessment?.issues.map((issue) => issue.message).join(" / ")}
            </span>
            <button
              className="pa-button pa-button-small"
              onClick={() => {
                setRunId(run.id);
                setAdvanced(true);
                setTab("results");
              }}
            >
              오류 상세
            </button>
          </div>
        ))}
      {quickOpen && (
        <IntegrationQuickStart
          lives={board.lives}
          runs={board.runs}
          initialSelected={selected}
          initialFocus={focus}
          busy={!!busy || stopping}
          busyLabel={busy}
          stopping={stopping}
          onStop={() => void stopAllTests()}
          error={quickError}
          onClose={() => setQuickOpen(false)}
          onSubmit={runQuick}
        />
      )}
      <IntegrationScenario
        board={board}
        busy={!!busy || stopping}
        onPrepare={async (settings) => {
          await perform("여러 라이브 준비", async () => {
            const { data } = await post("/lives/batch", settings);
            setSelected(data.lives);
            if (data.lives.length) setFocus(data.lives[0]);
            if (data.errors.length)
              throw new Error(
                `${data.lives.length}개 준비. ${data.errors.join(" / ")}`,
              );
          });
        }}
        onPublish={async (ids, durationSeconds) => {
          await perform("테스트 영상 송출", async () => {
            const { data } = await post("/publishers/start", {
              lives: ids,
              durationSeconds,
            });
            if (data.errors.length)
              throw new Error(
                `${data.accepted.length}개 접수. ${data.errors.join(" / ")}`,
              );
          });
        }}
        onRun={runQuick}
        onOpen={(live) => {
          setPopupLive(live.seq);
        }}
      />
      {(error || pollError) && (
        <div className="pa-inline-error" role="alert">
          {error || pollError}
        </div>
      )}
      {message && (
        <div className="pa-test-success" role="status">
          {message}
        </div>
      )}
      <details
        className="pa-test-advanced"
        open={advanced}
        onToggle={(event) => setAdvanced(event.currentTarget.open)}
      >
        <summary>전체 설정·실행 기록</summary>
        <div className="pa-test-navigation">
          <div
            className="pa-test-tabs"
            role="tablist"
            aria-label="통합 테스트 메뉴"
          >
            {tabs.map(([id, label], index) => (
              <button
                key={id}
                ref={(element) => {
                  tabRefs.current[index] = element;
                }}
                type="button"
                role="tab"
                id={`test-tab-${id}`}
                aria-controls={`test-panel-${id}`}
                aria-selected={tab === id}
                tabIndex={tab === id ? 0 : -1}
                onClick={() => setTab(id)}
                onKeyDown={(event) => {
                  let next = index;
                  if (event.key === "ArrowRight")
                    next = (index + 1) % tabs.length;
                  else if (event.key === "ArrowLeft")
                    next = (index - 1 + tabs.length) % tabs.length;
                  else if (event.key === "Home") next = 0;
                  else if (event.key === "End") next = tabs.length - 1;
                  else return;
                  event.preventDefault();
                  setTab(tabs[next][0]);
                  tabRefs.current[next]?.focus();
                }}
              >
                {label}
                {id === "results" && board.runs.some(active) && (
                  <span className="pa-test-tab-count">
                    {board.runs.filter(active).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="pa-test-summary">
          <div>
            <span>선택 라이브</span>
            <strong>
              {selectedCount}
              <small>개</small>
            </strong>
          </div>
          <div>
            <span>목표 동시 인원</span>
            <strong>
              {number(config.viewers * selectedCount)}
              <small>명</small>
            </strong>
          </div>
          <div>
            <span>현재 실행</span>
            <strong>
              {board.runs.filter(active).length}
              <small>개</small>
            </strong>
          </div>
          <div>
            <span>실행 서버</span>
            <strong className="pa-test-host">{board.runner.host}</strong>
            <small>
              {board.runner.chat
                ? "실시간 채팅 연결 설정됨"
                : "채팅 서버 설정 확인 필요"}
            </small>
          </div>
        </div>
        <div className="pa-test-workbench">
          <div className="pa-test-panels">
            <section
              className="pa-card"
              role="tabpanel"
              id="test-panel-lives"
              aria-labelledby="test-tab-lives"
              hidden={tab !== "lives"}
            >
              <SectionHeading
                title="테스트 라이브"
                description="셀러마다 IVS 채널 하나를 고정 배정합니다. 같은 셀러는 동시에 하나만 송출하고, 여러 셀러는 각자의 채널로 함께 테스트합니다."
              >
                <button
                  className="pa-button"
                  onClick={() => setCreateOpen((value) => !value)}
                >
                  {createOpen ? "예약 닫기" : "+ 테스트 라이브 예약"}
                </button>
              </SectionHeading>
              {createOpen && (
                <form
                  className="pa-test-create"
                  onSubmit={(event: FormEvent) => {
                    event.preventDefault();
                    if (quote === null) return;
                    void perform("테스트 라이브 예약", async () => {
                      const { data } = await post("/lives", {
                        ...create,
                        storeSeq: create.storeSeq || board.stores[0]?.seq,
                        testViewers: config.viewers,
                        warehouseProducts: config.warehouseProducts,
                        stockPerItem: config.stockPerItem,
                        start: iso(create.start),
                        finish: iso(create.finish),
                      });
                      setFocus(data.seq);
                      setSelected((current) => [...current, data.seq]);
                      setCreateOpen(false);
                      setQuote(null);
                      if (data.prepareError)
                        throw new Error(
                          `라이브 #${data.seq} 예약 완료. 자동 준비 재시도 필요: ${data.prepareError}`,
                        );
                    });
                  }}
                >
                  <label>
                    테스트 셀러
                    <select
                      value={create.storeSeq || board.stores[0]?.seq || ""}
                      onChange={(event) => {
                        setCreate({
                          ...create,
                          storeSeq: Number(event.target.value),
                        });
                        setQuote(null);
                      }}
                    >
                      {board.stores.map((store) => (
                        <option key={store.seq} value={store.seq}>
                          {store.store_name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    테스트 이름
                    <input
                      value={create.title}
                      maxLength={36}
                      required
                      onChange={(event) =>
                        setCreate({ ...create, title: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    예약 시작(KST)
                    <input
                      type="datetime-local"
                      step={3600}
                      value={create.start}
                      required
                      onChange={(event) => {
                        setCreate({ ...create, start: event.target.value });
                        setQuote(null);
                      }}
                    />
                  </label>
                  <label>
                    자동 송출 종료(KST)
                    <input
                      type="datetime-local"
                      step={3600}
                      value={create.finish}
                      required
                      onChange={(event) => {
                        setCreate({ ...create, finish: event.target.value });
                        setQuote(null);
                      }}
                    />
                  </label>
                  <label>
                    예약 인원
                    <select
                      value={create.capacity}
                      onChange={(event) => {
                        setCreate({
                          ...create,
                          capacity: Number(event.target.value),
                        });
                        setQuote(null);
                      }}
                    >
                      {[
                        50, 100, 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000,
                        4500, 5000,
                      ].map((value) => (
                        <option key={value} value={value}>
                          {number(value)}명
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="pa-test-create-actions">
                    <button
                      type="button"
                      className="pa-button"
                      disabled={!!busy}
                      onClick={() =>
                        void perform("예약 비용 조회", async () => {
                          const { data } = await post("/quote", {
                            ...create,
                            start: iso(create.start),
                            finish: iso(create.finish),
                          });
                          setQuote(data.coins);
                        })
                      }
                    >
                      예약 비용 확인
                    </button>
                    <button
                      className="pa-button pa-button-primary"
                      disabled={
                        !!busy || quote === null || !board.stores.length
                      }
                    >
                      {quote === null
                        ? "비용 확인 후 예약"
                        : `${number(quote)}코인으로 예약`}
                    </button>
                  </div>
                  <p className="pa-test-note">
                    창고 상품·옵션·재고·테스트 회원을 자동으로 준비합니다. 예약
                    코인은 기존 요금으로 차감됩니다. 예약 시간은 자동 종료와
                    비용 계산 기준이며, 테스트 라이브는 그 전후에도 다시 시작할
                    수 있습니다. 같은 셀러의 송출이 끝난 뒤 다른 라이브를 시작할
                    수 있습니다.
                  </p>
                </form>
              )}
              {!board.lives.length ? (
                <div className="pa-test-empty">
                  <strong>등록된 테스트 라이브가 없습니다.</strong>
                  <p>
                    테스트 라이브를 예약한 뒤 앱에서 해당 라이브로 송출할 수
                    있습니다.
                  </p>
                </div>
              ) : (
                <div className="pa-test-live-list">
                  {board.lives.map((live) => {
                    const running = board.runs.find(
                      (run) => run.liveSeq === live.seq && active(run),
                    );
                    return (
                      <article
                        key={live.seq}
                        className={`pa-test-live${focus === live.seq ? " is-focused" : ""}`}
                      >
                        <label className="pa-test-select">
                          <input
                            type="checkbox"
                            checked={selected.includes(live.seq)}
                            onChange={() => toggle(live.seq)}
                            aria-label={`${live.title} 선택`}
                          />
                          <span>
                            <button
                              type="button"
                              className="pa-test-live-title"
                              onClick={() => {
                                setFocus(live.seq);
                                setPopupLive(live.seq);
                              }}
                            >
                              {live.title}
                            </button>
                            <small>
                              #{live.seq} · {live.storeName} · 준비 계정{" "}
                              {number(live.prepared || 0)}명
                            </small>
                          </span>
                        </label>
                        <span
                          className={`pa-test-status ${running ? "is-running" : ""}`}
                        >
                          {running ? states[running.state] : live.status}
                        </span>
                        <div className="pa-test-live-actions">
                          <a
                            className="pa-button pa-button-small"
                            href={`/live/${live.seq}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            시청·전체 기능 ↗
                          </a>
                          <button
                            className="pa-button pa-button-small"
                            disabled={!!busy || !!running}
                            onClick={() =>
                              void perform("테스트 데이터 준비", () =>
                                post(`/lives/${live.seq}/prepare`, {
                                  count: config.viewers,
                                  warehouseProducts: config.warehouseProducts,
                                  stockPerItem: config.stockPerItem,
                                }),
                              )
                            }
                          >
                            창고·재고 다시 채우기
                          </button>
                          <button
                            className="pa-button pa-button-small"
                            disabled={!!busy}
                            onClick={() =>
                              void perform("IVS 송출 종료", () =>
                                post(`/lives/${live.seq}/end`),
                              )
                            }
                          >
                            송출 종료
                          </button>
                        </div>
                        {live.stopError && (
                          <p className="pa-inline-error">{live.stopError}</p>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
            <section
              className="pa-card"
              role="tabpanel"
              id="test-panel-load"
              aria-labelledby="test-tab-load"
              hidden={tab !== "load"}
            >
              <SectionHeading
                title="부하 시나리오"
                description="선택한 모든 라이브에서 채팅·판매 노출·구매·취소를 자동 반복합니다. 페이지를 닫아도 서버에서 실행됩니다."
              />
              <div className="pa-test-config">
                <Num
                  label="라이브당 동시 인원"
                  value={config.viewers}
                  min={1}
                  max={5000}
                  change={(value) => change("viewers", value)}
                />
                <Num
                  label="인원 증가 시간(초)"
                  value={config.rampSeconds}
                  min={1}
                  max={3600}
                  change={(value) => change("rampSeconds", value)}
                />
                <Num
                  label="실행 시간(초)"
                  value={config.durationSeconds}
                  min={10}
                  max={172800}
                  change={(value) => change("durationSeconds", value)}
                />
                <label>
                  부하 시작(KST)
                  <input
                    type="datetime-local"
                    value={startAt}
                    onChange={(event) => setStartAt(event.target.value)}
                  />
                  <small>비워두면 즉시 실행</small>
                </label>
                <Num
                  label="채팅 주기(초/명)"
                  value={config.chatEverySeconds}
                  min={1}
                  max={3600}
                  change={(value) => change("chatEverySeconds", value)}
                />
                <Num
                  label="조회 주기(초/명)"
                  value={config.apiEverySeconds}
                  min={1}
                  max={3600}
                  change={(value) => change("apiEverySeconds", value)}
                />
                <Num
                  label="구매 주기(초/명)"
                  value={config.purchaseEverySeconds}
                  min={1}
                  max={3600}
                  change={(value) => change("purchaseEverySeconds", value)}
                />
                <Num
                  label="시작톡 큐 건수/라이브"
                  value={config.sampleMessages}
                  min={0}
                  max={50000}
                  change={(value) => change("sampleMessages", value)}
                />
              </div>
              <div className="pa-test-config pa-test-auto-config">
                <Num
                  label="창고에서 가져올 상품 수"
                  value={config.warehouseProducts}
                  min={1}
                  max={5000}
                  change={(v) => change("warehouseProducts", v)}
                />
                <Num
                  label="상품·옵션별 보충 재고"
                  value={config.stockPerItem}
                  min={1}
                  max={1000000}
                  change={(v) => change("stockPerItem", v)}
                />
                <Num
                  label="셀러 상품 전환 주기(초)"
                  value={config.productEverySeconds}
                  min={1}
                  max={3600}
                  change={(v) => change("productEverySeconds", v)}
                />
              </div>
              <p className="pa-test-note">
                예약·실행 시 창고 상품과 계정을 자동으로 준비합니다. 창고에 있는
                상품이 설정 수보다 적으면 가져올 수 있는 상품을 모두 채웁니다.
                준비·종료 상태의 테스트 라이브는 실행 시 리허설로 자동
                전환합니다.
              </p>
              <div className="pa-test-scenarios">
                {(
                  [
                    [
                      "seller",
                      "셀러 상품 자동 전환",
                      "대기·판매·노출·가격 공개/비공개·품절",
                    ],
                    [
                      "autoStock",
                      "재고 자동 보충",
                      "상품·옵션을 보충하고 재고 원장 유지",
                    ],
                    [
                      "waitlist",
                      "재입고 대기·대기 취소",
                      "재입고 안내 후 실제 구매까지",
                    ],
                    ["video", "영상 시청", "실제 HLS 영상 조각 다운로드"],
                    ["chat", "채팅", "실제 접속·전송·ACK·수신"],
                    ["api", "목록·상세 조회", "라이브·상품·옵션·Q&A"],
                    ["orders", "다상품·다옵션 구매", "실제 주문 큐·수량 1~3개"],
                    ["cart", "장바구니·구매내역", "서비스의 실제 구매내역 API"],
                    ["cancel", "주문 취소", "취소권 사용·실제 재고 복원"],
                    [
                      "statements",
                      "종료 후 정산톡 큐",
                      "생성된 주문의 승인 문구 검증",
                    ],
                  ] as const
                ).map(([key, label, note]) => (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={config[key]}
                      onChange={(event) => change(key, event.target.checked)}
                    />
                    <span>
                      <strong>{label}</strong>
                      <small>{note}</small>
                    </span>
                  </label>
                ))}
              </div>
              <div className="pa-test-runbar">
                <p>
                  {number(selectedCount)}개 라이브 · 총{" "}
                  {number(config.viewers * selectedCount)}명 ·{" "}
                  {number(config.durationSeconds)}초<br />
                  <small>
                    알림은 생성·큐·검증만 실행합니다. 실제 발송 요청은
                    0건입니다.
                  </small>
                </p>
                <button
                  className="pa-button pa-button-primary"
                  disabled={!!busy || !selectedCount}
                  onClick={runAll}
                >
                  {busy === "선택 라이브 부하 실행"
                    ? "예약 중…"
                    : "선택 라이브 동시 실행"}
                </button>
              </div>
              <p className="pa-test-note">
                영상 부하는 네트워크 다운로드를 측정합니다. 영상 디코딩·실제
                기기 렌더링 성능은 앱·시청 화면에서 확인하세요. 구매·취소는 실제
                테스트 상품의 주문과 재고에 반영됩니다.
              </p>
            </section>
            <section
              className="pa-card"
              role="tabpanel"
              id="test-panel-results"
              aria-labelledby="test-tab-results"
              hidden={tab !== "results"}
            >
              <SectionHeading
                title="실행 결과"
                description="요청 성공과 실제 주문 완료를 구분합니다. 중지 후 이미 접수된 주문은 결과를 확인할 때까지 기다립니다."
              >
                <button
                  className="pa-button"
                  disabled={!currentRun}
                  onClick={exportRun}
                >
                  결과 JSON 다운로드
                </button>
              </SectionHeading>
              {!board.runs.length ? (
                <div className="pa-test-empty">아직 실행 기록이 없습니다.</div>
              ) : (
                <>
                  <div className="pa-test-result-toolbar">
                    <select
                      aria-label="실행 기록"
                      value={currentRun?.id || ""}
                      onChange={(event) => setRunId(event.target.value)}
                    >
                      {board.runs.map((run) => (
                        <option key={run.id} value={run.id}>
                          #{run.liveSeq} · {dateTime(run.createdAt)} ·{" "}
                          {states[run.state] || run.state}
                        </option>
                      ))}
                    </select>
                    {currentRun && active(currentRun) && (
                      <button
                        className="pa-button pa-button-danger"
                        disabled={!!busy}
                        onClick={() =>
                          void perform("부하 중지 요청", () =>
                            post(`/runs/${currentRun.id}/stop`),
                          )
                        }
                      >
                        이 실행 중지
                      </button>
                    )}
                  </div>
                  {currentRun && (
                    <>
                      <RunAssessment run={currentRun} />
                      <div className="pa-test-result-state">
                        <strong>{states[currentRun.state]}</strong>
                        <span>
                          갱신{" "}
                          {dateTime(
                            currentRun.updatedAt || currentRun.createdAt,
                          )}
                        </span>
                        <span>
                          채팅 접속 {number(currentRun.result?.connected)} ·
                          최대 {number(currentRun.result?.peakConnected)}
                        </span>
                        <span>
                          구매 완료 {number(currentRun.result?.ordersCompleted)}{" "}
                          · 미확정 {number(currentRun.result?.ordersUnknown)}
                        </span>
                      </div>
                      {currentRun.error && (
                        <p className="pa-inline-error">{currentRun.error}</p>
                      )}
                      <div className="pa-test-table-wrap">
                        <table className="pa-table">
                          <thead>
                            <tr>
                              <th>기능</th>
                              <th>요청</th>
                              <th>성공</th>
                              <th>실패</th>
                              <th>평균</th>
                              <th>P95 상한</th>
                              <th>최장</th>
                              <th>지연 기준 / 초과</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(
                              currentRun.result?.metrics || {},
                            ).map(([key, metric]) => (
                              <tr key={key}>
                                <td>{metricNames[key] || key}</td>
                                <td>{number(metric.attempts)}</td>
                                <td>{number(metric.success)}</td>
                                <td
                                  className={
                                    metric.failed ? "pa-test-error-value" : ""
                                  }
                                >
                                  {number(metric.failed)}
                                </td>
                                <td>
                                  {metric.meanMs === null
                                    ? "—"
                                    : `${number(metric.meanMs)}ms`}
                                </td>
                                <td>
                                  {metric.p95UpperMs === null
                                    ? "—"
                                    : `≤ ${number(metric.p95UpperMs)}ms`}
                                </td>
                                <td>
                                  {metric.maxMs == null
                                    ? "미측정"
                                    : `${number(metric.maxMs)}ms`}
                                </td>
                                <td
                                  className={
                                    metric.overBudget
                                      ? "pa-test-error-value"
                                      : ""
                                  }
                                >
                                  {metric.overBudget == null
                                    ? "미측정"
                                    : `${number(metric.latencyLimitMs)}ms / ${number(metric.overBudget)}건`}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {Object.entries(currentRun.notifications || {}).map(
                        ([kind, counts]) => (
                          <div className="pa-test-notification" key={kind}>
                            <strong>
                              {kind === "samples" ? "시작톡" : "정산톡"} · 발송
                              없는 큐 테스트
                            </strong>
                            <span>
                              목표 {number(counts.total)} / 큐{" "}
                              {number(counts.queued)} / 처리{" "}
                              {number(counts.processed)} / 검증{" "}
                              {number(counts.validated)} / 실패{" "}
                              {number(counts.failed)}
                            </span>
                            <b>실제 발송 {number(counts.providerRequests)}건</b>
                          </div>
                        ),
                      )}
                      {!!currentRun.result?.errors.length && (
                        <details>
                          <summary>발생한 오류</summary>
                          <ul>
                            {currentRun.result.errors.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                      <StockResult
                        inventory={inventory || currentRun.inventory}
                      />
                    </>
                  )}
                </>
              )}
            </section>
            <section
              className="pa-card"
              role="tabpanel"
              id="test-panel-functional"
              aria-labelledby="test-tab-functional"
              hidden={tab !== "functional"}
            >
              <SectionHeading
                title="실제 기능 점검"
                description="선택한 라이브의 채팅·공지·Q&A·구매·취소를 직접 실행합니다."
              >
                <select
                  aria-label="기능 점검 라이브"
                  value={focus}
                  onChange={(event) => setFocus(Number(event.target.value))}
                >
                  <option value={0}>라이브 선택</option>
                  {board.lives.map((live) => (
                    <option key={live.seq} value={live.seq}>
                      #{live.seq} {live.title}
                    </option>
                  ))}
                </select>
              </SectionHeading>
              {focus && detail ? (
                <>
                  <div className="pa-test-links">
                    <a href={`/live/${focus}`} target="_blank" rel="noreferrer">
                      실제 시청 페이지의 전체 기능 열기 ↗
                    </a>
                    <button
                      className="pa-button pa-button-small"
                      disabled={!!busy}
                      onClick={() =>
                        void perform("재고 대조", async () => {
                          const { data } = await post(
                            `/lives/${focus}/inventory`,
                          );
                          setInventory(data);
                        })
                      }
                    >
                      DB·Redis 재고 대조
                    </button>
                    <span>
                      {selectedLive?.status} · {detail.testUser}
                    </span>
                  </div>
                  <FunctionalTest
                    key={focus}
                    seq={focus}
                    detail={detail}
                    busy={!!busy}
                    act={(body) =>
                      perform("기능 실행", () =>
                        post(`/lives/${focus}/actions`, body),
                      )
                    }
                    fail={fail}
                  />
                </>
              ) : (
                <p className="pa-test-note">
                  라이브를 선택하면 실제 기능 점검 도구가 표시됩니다.
                </p>
              )}
            </section>
            <div
              role="tabpanel"
              id="test-panel-cost"
              aria-labelledby="test-tab-cost"
              hidden={tab !== "cost"}
            >
              <CostEstimate config={config} count={selectedCount} />
            </div>
          </div>
        </div>
      </details>
      {popupLive > 0 && (
        <IntegrationLivePopup
          key={popupLive}
          seq={popupLive}
          onClose={() => setPopupLive(0)}
        />
      )}
    </div>
  );
}
function Num({
  label,
  value,
  min,
  max,
  change,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  change: (value: number) => void;
}) {
  return (
    <label>
      {label}
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(event) => change(Number(event.target.value))}
      />
    </label>
  );
}
export function StockResult({ inventory }: { inventory?: Inventory | null }) {
  if (!inventory) return null;
  return (
    <div className="pa-test-inventory">
      <h3>
        최종 재고 대조{" "}
        <span
          className={
            inventory.passed ? "pa-test-success" : "pa-test-error-value"
          }
        >
          {inventory.passed ? "일치" : "확인 필요"}
        </span>
      </h3>
      <p>
        주문 접수증 {number(inventory.receipts)} · 주문 상세{" "}
        {number(inventory.details)} · 중복 {number(inventory.duplicateDetails)}{" "}
        · 누락 수량 {number(inventory.missingQuantity)} ·{" "}
        {dateTime(inventory.at)}
      </p>
      <div className="pa-test-table-wrap">
        <table className="pa-table">
          <thead>
            <tr>
              <th>상품·옵션</th>
              <th>초기</th>
              <th>구매</th>
              <th>복원</th>
              <th>예상</th>
              <th>DB</th>
              <th>Redis</th>
              <th>결과</th>
            </tr>
          </thead>
          <tbody>
            {inventory.items.map((item) => (
              <tr key={`${item.product}:${item.option}`}>
                <td>
                  {item.name} {item.optionName}
                </td>
                <td>{number(item.initial)}</td>
                <td>{number(item.sold)}</td>
                <td>{number(item.restored)}</td>
                <td>{number(item.expected)}</td>
                <td>{number(item.actual)}</td>
                <td>{number(item.redis)}</td>
                <td>{item.passed ? "일치" : "불일치"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FunctionalTest({
  seq,
  detail,
  busy,
  act,
  fail,
  viewerOnly = false,
  viewerIndex = 0,
}: {
  seq: number;
  detail: Detail;
  busy: boolean;
  act: (body: any) => Promise<unknown>;
  fail: (error: unknown) => void;
  viewerOnly?: boolean;
  viewerIndex?: number;
}) {
  const [role, setRole] = useState(viewerOnly ? "viewer" : "manager"),
    [connection, setConnection] = useState("연결 전"),
    [messages, setMessages] = useState<any[]>([]),
    [content, setContent] = useState(""),
    [index, setIndex] = useState(0),
    [words, setWords] = useState(""),
    [answer, setAnswer] = useState(""),
    [notice, setNotice] = useState(""),
    [noticeType, setNoticeType] = useState("ln"),
    [cart, setCart] = useState<any>(null),
    [choice, setChoice] = useState("");
  const socket = useRef<WebSocket | null>(null);
  useEffect(() => {
    socket.current?.close();
    socket.current = null;
    setConnection("연결 전");
    setMessages([]);
  }, [seq, viewerIndex]);
  useEffect(
    () => () => {
      socket.current?.close();
      socket.current = null;
    },
    [],
  );
  const connect = async () => {
    socket.current?.close();
    setConnection("연결 중");
    try {
      const { data } = await integrationApi.post(
        `${base}/lives/${seq}/ticket`,
        {
          viewer: role === "viewer",
          ...(role === "viewer" ? { index: viewerIndex } : {}),
        },
      );
      const ws = new WebSocket(data.endpoint, data.token);
      socket.current = ws;
      ws.onopen = () => {
        setConnection("연결됨");
        if (role === "manager")
          ws.send(
            JSON.stringify({
              action: "GET_HISTORY",
              requestId: "history",
              after: "0-0",
            }),
          );
      };
      ws.onclose = () => {
        if (socket.current === ws) setConnection("연결 종료 · 다시 연결 가능");
      };
      ws.onerror = () => setConnection("연결 실패");
      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === "batch" || payload.type === "history")
            setMessages((previous) =>
              [...previous, ...payload.messages].slice(-100),
            );
          if (payload.type === "error")
            setConnection(payload.message || "전송 거절");
        } catch {
          setConnection("응답 해석 실패");
        }
      };
    } catch (e) {
      setConnection("연결 실패");
      fail(e);
    }
  };
  const send = (event: FormEvent) => {
    event.preventDefault();
    if (socket.current?.readyState !== WebSocket.OPEN || !content.trim())
      return;
    socket.current.send(
      JSON.stringify({
        action: "SEND_MESSAGE",
        requestId: String(Date.now()),
        content,
        Attributes: { type: "user" },
      }),
    );
    setContent("");
  };
  const viewer = async (operation: string, extra: any = {}) => {
    try {
      const { data } = await integrationApi.post(
        `${base}/lives/${seq}/actions`,
        {
          action: "viewer",
          index,
          operation,
          ...extra,
        },
      );
      setCart(data);
    } catch (e) {
      fail(e);
    }
  };
  return (
    <div className="pa-test-functional">
      <div className="pa-test-chat">
        <div className="pa-test-chat-tools">
          <select
            aria-label="채팅 역할"
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            {!viewerOnly && <option value="manager">셀러 관리자</option>}
            <option value="viewer">테스트 회원 {viewerIndex + 1}</option>
          </select>
          <button
            className="pa-button pa-button-small"
            onClick={() => void connect()}
          >
            채팅 연결·재연결
          </button>
          <span role="status">{connection}</span>
        </div>
        <div className="pa-test-chat-log" aria-label="실시간 채팅">
          {!messages.length ? (
            <p>채팅을 연결하면 실제 메시지가 표시됩니다.</p>
          ) : (
            messages.map((item, i) => (
              <article key={item.id || i}>
                <b>
                  {item.Sender?.Attributes?.nickname ||
                    item.Sender?.UserId ||
                    "시스템"}
                </b>
                <span>{item.Content}</span>
                {item.Attributes?.moderation && (
                  <small>{item.Attributes.moderation}</small>
                )}
                <button
                  className="pa-button pa-button-small"
                  hidden={viewerOnly}
                  onClick={() =>
                    void act({
                      action: "qa-answer",
                      messageId: item.id,
                      answer,
                    })
                  }
                  disabled={busy || !answer}
                >
                  Q&A 답변
                </button>
              </article>
            ))
          )}
        </div>
        <form onSubmit={send}>
          <input
            aria-label="테스트 채팅 메시지"
            value={content}
            onChange={(event) => setContent(event.target.value)}
            maxLength={1000}
            placeholder="실제 채팅 메시지"
          />
          <button
            className="pa-button"
            disabled={!content.trim() || connection !== "연결됨"}
          >
            전송
          </button>
        </form>
        <label hidden={viewerOnly}>
          Q&A 답변
          <input
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            placeholder="답변 입력 후 위 채팅에서 Q&A 답변 선택"
          />
        </label>
      </div>
      {!viewerOnly && (
        <>
          <div className="pa-test-manual" hidden={viewerOnly}>
            <h3>회원 제어·구매</h3>
            <Num
              label="테스트 회원 번호(0부터)"
              value={index}
              min={0}
              max={4999}
              change={setIndex}
            />
            <div className="pa-test-button-row">
              {(["mute", "ban"] as const).flatMap((action) =>
                [true, false].map((enabled) => (
                  <button
                    key={`${action}:${enabled}`}
                    className="pa-button pa-button-small"
                    disabled={busy}
                    onClick={() => void act({ action, index, enabled })}
                  >
                    {action === "mute" ? "벙어리" : "차단"}{" "}
                    {enabled ? "설정" : "해제"}
                  </button>
                )),
              )}
            </div>
            <label>
              테스트 채널 금칙어(쉼표로 구분)
              <input
                value={words}
                onChange={(event) => setWords(event.target.value)}
              />
            </label>
            <button
              className="pa-button pa-button-small"
              disabled={busy}
              onClick={() =>
                void act({
                  action: "words",
                  words: words
                    .split(",")
                    .map((word) => word.trim())
                    .filter(Boolean),
                })
              }
            >
              금칙어 저장
            </button>
            <label>
              테스트 상품·옵션
              <select
                value={choice}
                onChange={(event) => setChoice(event.target.value)}
              >
                <option value="">상품 선택</option>
                {detail.products.map((item) => (
                  <option
                    key={`${item.seq}:${item.option_seq}`}
                    value={`${item.seq}:${item.option_seq || ""}`}
                  >
                    {item.name} {item.option1} · 재고{" "}
                    {number(
                      Number(item.option_current_cnt ?? item.current_cnt),
                    )}
                  </option>
                ))}
              </select>
            </label>
            <div className="pa-test-button-row">
              <button
                className="pa-button"
                disabled={busy || !choice}
                onClick={() =>
                  void viewer("buy", {
                    product: Number(choice.split(":")[0]),
                    option: Number(choice.split(":")[1]) || undefined,
                  })
                }
              >
                1개 구매 요청
              </button>
              <button
                className="pa-button"
                disabled={busy}
                onClick={() => void viewer("cart")}
              >
                장바구니·구매내역 조회
              </button>
            </div>
            {cart && (
              <details open>
                <summary>실제 API 결과</summary>
                <pre>{JSON.stringify(cart, null, 2)}</pre>
              </details>
            )}
            <label>
              취소할 주문 상세 번호
              <input type="number" id={`cancel-${seq}`} />
            </label>
            <button
              className="pa-button pa-button-small"
              disabled={busy}
              onClick={() => {
                const input = document.getElementById(
                  `cancel-${seq}`,
                ) as HTMLInputElement;
                void viewer("cancel", { detail: Number(input.value) });
              }}
            >
              선택 회원 주문 1개 취소
            </button>
          </div>
          <div className="pa-test-notices">
            <h3>공지·Q&A</h3>
            <div className="pa-test-button-row">
              <select
                aria-label="공지 유형"
                value={noticeType}
                onChange={(event) => setNoticeType(event.target.value)}
              >
                <option value="ln">라이브 공지</option>
                <option value="rn">상시 공지</option>
                <option value="sn">셀러 공지</option>
              </select>
              <input
                aria-label="공지 내용"
                value={notice}
                onChange={(event) => setNotice(event.target.value)}
                placeholder="공지 내용"
              />
              <button
                className="pa-button"
                disabled={busy || !notice}
                onClick={() =>
                  void act({
                    action: "notice",
                    type: noticeType,
                    title: "통합 테스트",
                    content: notice,
                  })
                }
              >
                등록
              </button>
            </div>
            {detail.notices.map((item) => (
              <div className="pa-test-notice-item" key={item.seq}>
                <span>
                  {item.type} · {item.content}
                </span>
                <button
                  className="pa-button pa-button-small"
                  disabled={busy || !notice}
                  onClick={() =>
                    void act({
                      action: "notice-edit",
                      id: item.seq,
                      content: notice,
                      title: "통합 테스트",
                    })
                  }
                >
                  수정
                </button>
                <button
                  className="pa-button pa-button-small"
                  disabled={busy}
                  onClick={() =>
                    void act({ action: "notice-delete", id: item.seq })
                  }
                >
                  삭제
                </button>
              </div>
            ))}
            {detail.qa.map((item) => (
              <div className="pa-test-notice-item" key={item.id}>
                <span>
                  {item.question} → {item.answer}
                </span>
                <button
                  className="pa-button pa-button-small"
                  disabled={busy || !answer}
                  onClick={() =>
                    void act({
                      action: "qa-edit",
                      id: item.id,
                      question: item.question,
                      answer,
                    })
                  }
                >
                  답변 수정
                </button>
                <button
                  className="pa-button pa-button-small"
                  disabled={busy}
                  onClick={() => void act({ action: "qa-delete", id: item.id })}
                >
                  삭제
                </button>
              </div>
            ))}
            <div className="pa-test-product-controls">
              <h3>상품·가격 노출</h3>
              {detail.products
                .filter(
                  (item, i, all) =>
                    all.findIndex((other) => other.seq === item.seq) === i,
                )
                .map((item) => (
                  <div key={item.seq}>
                    <span>{item.name}</span>
                    {["대기", "판매", "판매노출", "품절", "품절노출"].map(
                      (status) => (
                        <button
                          key={status}
                          className="pa-button pa-button-small"
                          disabled={busy}
                          onClick={() =>
                            void act({
                              action: "product",
                              id: item.seq,
                              status,
                              priceMode: item.exposure_price_mode,
                            })
                          }
                        >
                          {status}
                        </button>
                      ),
                    )}
                    <button
                      className="pa-button pa-button-small"
                      disabled={busy}
                      onClick={() =>
                        void act({
                          action: "product",
                          id: item.seq,
                          status: item.product_status,
                          priceMode:
                            item.exposure_price_mode === "visible"
                              ? "seller"
                              : "visible",
                        })
                      }
                    >
                      {item.exposure_price_mode === "visible"
                        ? "가격 비공개"
                        : "가격 공개"}
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
function CostEstimate({ config, count }: { config: Config; count: number }) {
  const [input, setInput] = useState(2),
    [output, setOutput] = useState(0.25),
    [infrastructure, setInfrastructure] = useState(""),
    [exchange, setExchange] = useState("");
  const hours = config.durationSeconds / 3600,
    viewerHours = config.video
      ? (count *
          config.viewers *
          Math.max(0, config.durationSeconds - config.rampSeconds / 2)) /
        3600
      : 0;
  const media = count * hours * input + viewerHours * output,
    total = media + (Number(infrastructure) || 0) * hours;
  return (
    <section className="pa-card">
      <SectionHeading
        title="비용 예상"
        description="설정값 기준 추정입니다. 실제 청구는 송출 시간·서버 위치·화질·할인에 따라 달라집니다."
      />
      <div className="pa-test-config">
        <label>
          IVS 입력 USD/채널·시간
          <input
            type="number"
            min={0}
            step="0.01"
            value={input}
            onChange={(event) => setInput(Number(event.target.value))}
          />
        </label>
        <label>
          IVS 출력 USD/시청·시간
          <input
            type="number"
            min={0}
            step="0.001"
            value={output}
            onChange={(event) => setOutput(Number(event.target.value))}
          />
        </label>
        <label>
          서버·Redis·DB 합계 USD/시간
          <input
            type="number"
            min={0}
            step="0.01"
            value={infrastructure}
            onChange={(event) => setInfrastructure(event.target.value)}
            placeholder="계약 요금 입력"
          />
        </label>
        <label>
          원/USD 환율
          <input
            type="number"
            min={0}
            value={exchange}
            onChange={(event) => setExchange(event.target.value)}
            placeholder="적용 환율 입력"
          />
        </label>
      </div>
      <div className="pa-test-cost">
        <strong>약 ${total.toFixed(2)} USD</strong>
        {exchange && (
          <span>약 {number(Math.round(total * Number(exchange)))}원</span>
        )}
        <span>시작톡·정산톡 실제 발송 비용 0원</span>
      </div>
      <p className="pa-test-note">
        기본값: Standard 입력 $2/시간, 한국 Full HD 첫 10,000시간 출력
        $0.25/시간.{" "}
        <a
          href="https://aws.amazon.com/ivs/pricing/"
          target="_blank"
          rel="noreferrer"
        >
          AWS 공식 요금표(2026-10-21 확인) ↗
        </a>
        <br />
        {infrastructure
          ? "입력한 인프라 비용 포함."
          : "인프라 요금 미입력: 서버·Redis·DB·실행기 네트워크 요금 제외."}{" "}
        예약 코인과 AWS 비용은 별도입니다.
      </p>
    </section>
  );
}
